/* global React, Icon, Card, CardHead, EmptyState, KPI, Button, IconButton, Checkbox, Input, ProgressBar, StatusBadge, Modal, toast */
// ============================================================
// PLUMME 2.0 — Dashboard (Hjem)
// ============================================================
// Samme array-referanse som MONTHS i data.jsx — oppdateres på plass ved språkbytte.
const MONTH_LABELS = MONTHS;
const homeMonthLabel = (index) => t('month.short.' + (index + 1), MONTH_LABELS[index]);
const EMPTY_HOME_MODEL = {
  chart: Array.from({ length: 12 }, () => ({ inc: 0, cost: 0 })),
  summary: { income: 0, costs: 0, result: 0, previousIncome: 0, previousCosts: 0, previousResult: 0 },
  overview: { salesCurrent: 0, salesOverdue: 0, purchasesCurrent: 0, purchasesOverdue: 0 },
  deadlines: [],
  followup: { unsentInvoices: 0, overdueInvoices: 0, missingAttachmentCount: 0, openPurchaseCount: 0 },
  publicFees: { vatDue: 0, taxBuffer: 0, rows: [] },
  analysis: {
    incomeYoy: null,
    costsYoy: null,
    resultYoy: null,
    liquidityRatio: null,
    profitability: null,
    solidity: null,
  },
};
const MAX_VISIBLE_FOLLOW_UP_ITEMS = 3;
const getHomeModel = () => window.PlummeBackend?.getHomeDashboardModel?.() || EMPTY_HOME_MODEL;
const routeForDeadline = (deadline) => {
  const id = deadline?.id || '';
  if (id === 'taxSubmitted') return 'skattemelding';
  if (id === 'annualAccountsSubmitted') return 'arsregnskap';
  if (id === 'annualClosingSubmitted') return 'arsavslutning';
  // Aksjonærregisteroppgaven har ingen egen flate i Plumme. Skjemaet sendes fra
  // et sluttbrukersystem eller Altinn, og lenka peker dit — ikke til en side som
  // later som om den kan levere den.
  if (id === 'shareholderRegisterSubmitted') return 'altinn';
  return 'innsendinger';
};
// Tallformat følger språk, valutasuffiks følger foretakets land. Beholder sidens
// eget bindestrek-minus i stedet for PlummeI18n.money sitt typografiske minus.
const formatHomeCurrency = (n) => {
  const value = Number(n) || 0;
  return (value < 0 ? '-' : '') + PlummeI18n.number(Math.round(Math.abs(value))) + ' ' + PlummeI18n.currencySuffix();
};
const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
const pctChg = (cur, pre) => pre ? Math.round((cur - pre) / Math.abs(pre) * 1000) / 10 : null;
const pctStr = (n) => n == null ? t('home.noData') : PlummeI18n.number(n, oneDecimal);
const ratioStr = (n) => n == null ? t('home.noData') : PlummeI18n.number(n, oneDecimal) + 'x';
const percentValueStr = (n) => n == null ? t('home.noData') : PlummeI18n.number(n, oneDecimal) + ' %';
// Soft rolling purple wave behind bar charts — follows the bars (flat where there is no activity), fully rounded (Catmull-Rom)
function smoothLine(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i],p1 = pts[i],p2 = pts[i + 1],p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6,c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6,c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}
// Nøkkeltallene slås opp på stabil id, ikke på visningstittelen — tittelen er
// oversatt og kan ikke brukes som oppslagsnøkkel.
const homeGaugeMeta = (id) => ({
  title: t('home.gauge.' + id),
  info: t('home.gauge.' + id + '.info'),
  good: t('home.gauge.' + id + '.good'),
});
const deadlineHelpText = (deadline) => {
  const id = deadline?.id || '';
  if (id === 'taxSubmitted') return t('home.deadlines.helpTax');
  if (id === 'annualAccountsSubmitted') return t('home.deadlines.helpAnnualAccounts');
  if (id === 'annualClosingSubmitted') return t('home.deadlines.helpAnnualClosing');
  if (id === 'shareholderRegisterSubmitted') return t('home.deadlines.helpShareholderRegister');
  if (String(id).toLowerCase().includes('vat') || id === 'mvaSubmitted' || /^term-\d+$/.test(id)) return t('home.deadlines.helpVat');
  return t('home.deadlines.helpFallback', { name: deadlineDisplayName(deadline) || t('home.deadlines.fallbackName') });
};
const deadlineDisplayName = (deadline) => {
  const id = deadline?.id || '';
  if (id === 'shareholderRegisterSubmitted') return t('home.deadlines.name.shareholderRegister');
  if (id === 'taxSubmitted') return t('nav.taxReturn');
  if (id === 'annualAccountsSubmitted') return t('nav.annualAccounts');
  if (id === 'mvaSubmitted') return t('nav.filings');
  const vatTerm = id.match(/^term-(\d+)$/);
  if (vatTerm) return `${t('nav.filings')} (${vatTerm[1]})`;
  return deadline?.name || '';
};
const deadlineDisplayDate = (deadline) => deadline?.dueDate
  ? PlummeI18n.date(deadline.dueDate, { day: 'numeric', month: 'short', year: 'numeric' })
  : deadline?.date || '';

const DONUT_PALETTE = ['var(--plum-700)', 'var(--plum-500)', 'var(--warning)', 'var(--success)', 'var(--plum-400)', 'var(--plum-300)'];
const donutRows = (rows, fallbackLabel, total) => {
  const cleanRows = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      label: row?.label || row?.name || row?.accountName || fallbackLabel,
      amount: Math.max(0, Number(row?.amount) || 0),
    }))
    .filter((row) => row.amount > 0);
  const sum = cleanRows.reduce((acc, row) => acc + row.amount, 0);
  if (!sum) return [{ label: fallbackLabel, amount: Math.max(0, Number(total) || 0), fraction: 1 }];
  return cleanRows.map((row) => ({ ...row, fraction: row.amount / sum }));
};
function Donut({ drill, setDrill, income, costs, result }) {
  const incTot = income;
  const costTot = costs;
  const resTot = result;
  const tot = incTot + costTot || 1;const r = 95,c = 2 * Math.PI * r;
  const incF = incTot / tot,costF = costTot / tot;
  const INC = donutRows(null, t('home.donut.registeredIncome'), incTot);
  const COST = donutRows(null, t('home.donut.registeredCosts'), costTot);
  const sel = drill === 'inc' ? { title: t('home.donut.income'), tot: incTot, color: 'var(--success)', rows: INC }
    : drill === 'cost' ? { title: t('home.donut.costs'), tot: costTot, color: 'var(--warning)', rows: COST } : null;
  const sw = (k) => drill === k ? 42 : 34;
  const [part, setPart] = React.useState(null);
  React.useEffect(() => { setPart(null); }, [drill]);
  const centerLabel = sel ? (part != null ? sel.rows[part]?.label : sel.title) : t('home.donut.result');
  const centerVal = sel ? (part != null ? sel.rows[part]?.amount : sel.tot) : resTot;
  const centerColor = sel ? (part != null ? DONUT_PALETTE[part % DONUT_PALETTE.length] : sel.color) : 'var(--ink)';
  return <div className="col" style={{ alignItems: 'center', gap: 20, padding: '8px 0 4px' }}>
    <svg width="300" height="300" viewBox="0 0 240 240" style={{ maxWidth: '100%' }}>
      <circle cx="120" cy="120" r={r} fill="none" stroke="var(--panel-2)" strokeWidth="34" />
      {sel ? (() => { let acc = 0; return sel.rows.map((row, i) => {
        const col = DONUT_PALETTE[i % DONUT_PALETTE.length];
        const seg = <circle data-app-control="true" key={row.label} cx="120" cy="120" r={r} fill="none" stroke={col}
          strokeWidth={part === i ? 44 : 34} strokeDasharray={`${Math.max(0, c * row.fraction - 1.5)} ${c}`} strokeDashoffset={-c * acc}
          transform="rotate(-90 120 120)" strokeLinecap="butt"
          style={{ cursor: 'pointer', transition: 'stroke-width .15s,opacity .15s', opacity: part != null && part !== i ? .35 : 1 }}
          onClick={() => setPart(part === i ? null : i)}>
          <title>{row.label}: {formatHomeCurrency(row.amount)}</title>
        </circle>;
        acc += row.fraction; return seg;
      }); })() : <React.Fragment>
      <circle data-app-control="true" cx="120" cy="120" r={r} fill="none" stroke="var(--warning)" strokeWidth={sw('cost')}
        strokeDasharray={`${c * costF} ${c}`} transform="rotate(-90 120 120)" strokeLinecap="butt"
        style={{ cursor: 'pointer', transition: 'stroke-width .15s', opacity: drill && drill !== 'cost' ? .4 : 1 }}
        onClick={() => setDrill(drill === 'cost' ? null : 'cost')} />
      <circle data-app-control="true" cx="120" cy="120" r={r} fill="none" stroke="var(--success)" strokeWidth={sw('inc')}
        strokeDasharray={`${c * incF} ${c}`} strokeDashoffset={-c * costF} transform="rotate(-90 120 120)"
        style={{ cursor: 'pointer', transition: 'stroke-width .15s', opacity: drill && drill !== 'inc' ? .4 : 1 }}
        onClick={() => setDrill(drill === 'inc' ? null : 'inc')} />
      </React.Fragment>}
      <text data-app-control="true" x="120" y="112" textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--muted)" style={{ cursor: drill ? 'pointer' : 'default' }} onClick={() => (part != null ? setPart(null) : setDrill(null))}>{centerLabel}</text>
      <text x="120" y="138" textAnchor="middle" fontSize="22" fontWeight="900" fill={centerColor}>{formatHomeCurrency(centerVal)}</text>
    </svg>
    {sel && <div style={{ width: '100%', maxWidth: 400 }}>
        <div className="row between" style={{ marginBottom: 12 }}><b className="t-h3">{t('home.donut.breakdown', { title: sel.title })}</b><button className="link-reset" onClick={() => setDrill(null)}>{t('home.donut.back')}</button></div>
        {sel.rows.map((row, i) => { const col = DONUT_PALETTE[i % DONUT_PALETTE.length]; return <div data-app-control="true" key={row.label} onClick={() => setPart(part === i ? null : i)}
          style={{ marginBottom: 4, padding: '7px 9px', borderRadius: 'var(--r-sm)', cursor: 'pointer', background: part === i ? 'var(--plum-50)' : 'transparent', opacity: part != null && part !== i ? .5 : 1, transition: 'background .13s,opacity .13s' }}>
          <div className="row between t-sm" style={{ fontWeight: 700, marginBottom: 5 }}><span className="row gap-8" style={{ alignItems: 'center' }}><span style={{ width: 11, height: 11, borderRadius: 'var(--r-3xs)', background: col, flex: 'none' }} />{row.label}</span><span className="t-num">{formatHomeCurrency(row.amount)}</span></div>
          <div style={{ height: 8, borderRadius: 'var(--r-3xs)', background: 'var(--panel-2)' }}><div style={{ height: '100%', width: (row.fraction * 100) + '%', borderRadius: 'var(--r-3xs)', background: col }} /></div>
        </div>; })}
      </div>}
    {!sel && <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('home.donut.hint')}</div>}
  </div>;
}
function Gauge({ title, label, value, pct, yoy, info, good, onClick }) {
  const cx = 90,cy = 96,r = 66;
  const normalizedPct = pct == null ? 0.5 : Math.max(0, Math.min(1, pct));
  const ang = Math.PI * (1 - normalizedPct);
  const nx = cx + Math.cos(ang) * (r - 14),ny = cy - Math.sin(ang) * (r - 14);
  const arc = (a0, a1, color) => {
    const x0 = cx + Math.cos(a0) * r,y0 = cy - Math.sin(a0) * r,x1 = cx + Math.cos(a1) * r,y1 = cy - Math.sin(a1) * r;
    return <path d={`M ${x0.toFixed(1)} ${y0.toFixed(1)} A ${r} ${r} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`} fill="none" stroke={color} strokeWidth="13" strokeLinecap="round" />;
  };
  return <div data-app-control="true" className="col" onClick={onClick} onMouseEnter={(e) => { if (info) e.currentTarget.style.background = 'var(--panel)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    style={{ alignItems: 'center', gap: 3, cursor: info ? 'pointer' : 'default', borderRadius: 'var(--r-md)', padding: '4px 2px', transition: 'background .14s' }} title={info ? t('home.gauge.explain') : undefined}>
    <div className="eyebrow row" style={{ gap: 5, alignItems: 'center', justifyContent: 'center' }}>{title}{info && <Icon name="info" size={13} style={{ opacity: 0.5 }} />}</div>
    <svg width="180" height="118" viewBox="0 0 180 118">
      {arc(Math.PI, Math.PI * 0.63, 'var(--warning)')}
      {arc(Math.PI * 0.6, Math.PI * 0.4, 'var(--plum-400)')}
      {arc(Math.PI * 0.37, 0, 'var(--success)')}
      <line x1={cx} y1={cy} x2={nx.toFixed(1)} y2={ny.toFixed(1)} stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="6.5" fill="none" stroke="var(--ink)" strokeWidth="3" />
      <circle cx={cx} cy={cy} r="2" fill="var(--ink)" />
    </svg>
    <b className="t-h3" style={{ fontSize: 16 }}>{label}</b>
    <div className="t-sm muted" style={{ fontWeight: 700 }}>{value}</div>
    {yoy != null && <div className="t-sm" style={{ fontWeight: 800, color: yoy >= 0 ? 'var(--success)' : 'var(--error)', display: 'flex', alignItems: 'center', gap: 4 }}>
      <Icon name={yoy >= 0 ? 'trendUp' : 'trendDown'} size={13} />{yoy >= 0 ? '+' : ''}{pctStr(yoy)} %<span className="muted" style={{ fontWeight: 700 }}>{t('home.fromLastYear')}</span></div>}
  </div>;
}
const healthLabel = (value, goodAt = 0.6) => value == null ? t('home.noData') : value >= goodAt ? t('home.health.good') : value >= goodAt / 2 ? t('home.health.balanced') : t('home.health.weak');
const buildDistributionGauges = (model) => {
  const { income, costs, result } = model.summary;
  const max = Math.max(Math.abs(income), Math.abs(costs), Math.abs(result), 1);
  return [
    { id: 'income', label: healthLabel(income / max), value: formatHomeCurrency(income), pct: income / max, yoy: model.analysis.incomeYoy },
    { id: 'costs', label: healthLabel(1 - costs / max), value: formatHomeCurrency(costs), pct: 1 - costs / max, yoy: model.analysis.costsYoy },
    { id: 'result', label: healthLabel((result / max + 1) / 2), value: formatHomeCurrency(result), pct: (result / max + 1) / 2, yoy: model.analysis.resultYoy },
  ].map((g) => ({ ...g, ...homeGaugeMeta(g.id) }));
};
const buildFinancialGauges = (model) => [
  { id: 'liquidity', label: healthLabel(model.analysis.liquidityRatio, 1), value: ratioStr(model.analysis.liquidityRatio), pct: model.analysis.liquidityRatio == null ? null : Math.min(1, model.analysis.liquidityRatio / 2), yoy: null },
  { id: 'profitability', label: healthLabel(model.analysis.profitability, 15), value: percentValueStr(model.analysis.profitability), pct: model.analysis.profitability == null ? null : Math.min(1, Math.max(0, model.analysis.profitability) / 30), yoy: null },
  { id: 'solidity', label: healthLabel(model.analysis.solidity, 30), value: percentValueStr(model.analysis.solidity), pct: model.analysis.solidity == null ? null : Math.min(1, Math.max(0, model.analysis.solidity) / 60), yoy: null },
].map((g) => ({ ...g, ...homeGaugeMeta(g.id) }));
function GaugeRow({ data }) {
  const [info, setInfo] = useState(null);
  return <>
    <div className="grid-3" style={{ gap: 12, padding: '6px 0' }}>{data.map((g) => <Gauge key={g.title} {...g} onClick={g.info ? () => setInfo(g) : undefined} />)}</div>
    <Modal open={!!info} onClose={() => setInfo(null)} max={520} title={info ? info.title : ''} footer={<Button onClick={() => setInfo(null)}>{t('common.close')}</Button>}>
      {info && <>
        <div className="row gap-16" style={{ marginBottom: 16, alignItems: 'center' }}>
          <div><div className="eyebrow">{t('home.gauge.currentValue')}</div><div className="t-h2" style={{ fontSize: 24, fontWeight: 900 }}>{info.value}</div></div>
          <div style={{ flex: 1 }} />
          <div style={{ textAlign: 'right' }}><div className="eyebrow">{t('home.gauge.assessment')}</div><div className="t-h3">{info.label}</div></div>
        </div>
        <p style={{ lineHeight: 1.65, color: 'var(--ink-2)', fontWeight: 500 }}>{info.info}</p>
        {info.good && <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--success-bg)', border: '1px solid var(--success-bd)', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 700, color: 'var(--success)' }}>{t('home.gauge.goodLevel', { good: info.good })}</div>}
      </>}
    </Modal>
  </>;
}
// Status ligger inne i «AI Regnskapsfører»-knappen, ikke som egen pille ved siden av.
// Av/på leses fra autopilot-bryteren (`settings.autopilot.enabled`), «må følges opp»
// fra oppfølgingslista i modellen. `plumme-backend-state` fanger endringer som kommer
// fra en annen enhet eller fra bytte av foretak.
function AutopilotStatus({ followup }) {
  const readOn = () => (window.apGetOn ? window.apGetOn() : true);
  const [on, setOn] = useState(readOn);
  useEffect(() => {
    const handleChange = () => setOn(readOn());
    window.addEventListener('plumme-autopilot-changed', handleChange);
    window.addEventListener('plumme-backend-state', handleChange);
    window.addEventListener('storage', handleChange);
    return () => {
      window.removeEventListener('plumme-autopilot-changed', handleChange);
      window.removeEventListener('plumme-backend-state', handleChange);
      window.removeEventListener('storage', handleChange);
    };
  }, []);
  const count = ((followup?.groups || []).reduce((sum, group) => sum + (group.items || []).length, 0));
  const tone = !on ? 'off' : count > 0 ? 'warn' : 'on';
  const label = !on ? t('home.autopilot.disabled') : count > 0 ? t('home.autopilot.followUp') : t('home.autopilot.active');
  return <span className="ap-btn-status"><span className={'ap-status-circle ' + tone} />{label}</span>;
}

// The home card shares the persistent app-control conversation and executor.
function Assistant() {
  const control = React.useContext(SuperPlummaContext);
  return control && window.innerWidth > 610 ? <SuperPlummaChat {...control} /> : null;
}

function followupItemText(item) {
  if (typeof item === 'string') return item;
  if (item?.i18nKey === 'home.followup.missingBankStatements') {
    const monthFormatter = new Intl.DateTimeFormat(PlummeI18n.intlLocale(), { month: 'long', timeZone: 'UTC' });
    const months = (item.monthIndexes || [])
      .map((monthIndex) => monthFormatter.format(new Date(Date.UTC(2020, monthIndex, 1))))
      .join(', ');
    return t('home.followup.missingBankStatements', item.text || '', { months });
  }
  return item?.text || '';
}

const localizeFollowupGroup = (group) => group?.id === 'bank' ? {
  ...group,
  title: t('home.followup.bank', group.title),
  actionLabel: t('home.followup.toBank', group.actionLabel),
} : group;

function FollowupRow({ tone, item, onOpen }) {
  const text = followupItemText(item);
  const clickable = Boolean(item && typeof item === 'object' && item.route);
  const open = () => clickable && onOpen && onOpen(item);
  // De fleste punktene er ting som venter. Et kritisk punkt er noe som gir feil i
  // regnskapet mens det staar ubehandlet, og skal skille seg fra koen.
  const critical = (item && typeof item === 'object' && item.tone === 'critical') || tone === 'critical';
  return <div className={'alert ' + (critical ? 'alert-error' : 'alert-warning')} role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
    onClick={open} onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(); } }}
    style={{ padding: '11px 13px', fontSize: 13.5, cursor: clickable ? 'pointer' : undefined }}>
    <span className="alert-ico"><Icon name={critical ? 'alertTri' : 'alertCircle'} size={18} /></span><span className="grow" style={{ fontWeight: 700 }}>{text}</span>
    {clickable && <Icon name="chevRight" size={16} style={{ color: critical ? 'var(--error)' : 'var(--warning)' }} />}
  </div>;
}

function FollowupCard({ followup, setRoute }) {
  const [modalOpen, setModalOpen] = useState(false);
  const fallbackGroups = [
    {
      id: 'sales',
      title: t('home.followup.sales'),
      route: 'fakturaliste',
      actionLabel: t('home.followup.toInvoiceList'),
      items: [
        (followup.draftInvoices || 0) > 0 ? t('home.followup.draftInvoices', { n: followup.draftInvoices }) : null,
        followup.unsentInvoices > 0 ? t('home.followup.unsentInvoices', { n: followup.unsentInvoices }) : null,
        followup.overdueInvoices > 0 ? t('home.followup.overdueInvoices', { n: followup.overdueInvoices }) : null,
      ].filter(Boolean),
    },
    {
      id: 'purchases',
      title: t('home.followup.purchases'),
      route: 'allebilag',
      actionLabel: t('home.followup.toVoucherList'),
      items: [
        (followup.openPurchaseCount || 0) > 0 ? t('home.followup.openPurchases', { n: followup.openPurchaseCount }) : null,
        followup.missingAttachmentCount > 0 ? t('home.followup.missingAttachments', { n: followup.missingAttachmentCount }) : null,
      ].filter(Boolean),
    },
  ].filter((group) => group.items.length > 0);
  const groups = (Array.isArray(followup.groups) && followup.groups.length ? followup.groups : fallbackGroups)
    .map(localizeFollowupGroup);
  const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);
  let visibleCount = 0;
  const visibleGroups = groups
    .map((group) => {
      const remaining = Math.max(MAX_VISIBLE_FOLLOW_UP_ITEMS - visibleCount, 0);
      const visibleItems = group.items.slice(0, remaining);
      visibleCount += visibleItems.length;
      return { ...group, visibleItems };
    })
    .filter((group) => group.visibleItems.length > 0);
  const openRoute = (group) => {
    setModalOpen(false);
    if (group?.targetTab || group?.targetInvoiceFilter) {
      window.__plummeInvoiceListFilter = { tab: group.targetTab, ...(group.targetInvoiceFilter || {}) };
      if (group?.targetTab) window.__plummeInvoiceListTab = group.targetTab;
    }
    if (group?.targetVoucherFilter) window.__plummeVoucherFilter = group.targetVoucherFilter;
    if (group?.targetSupplierFilter) window.__plummeSupplierFilter = group.targetSupplierFilter;
    if (group?.route) setRoute(group.route);
  };
  return <Card>
    {/* Backend beholder norske fallbacktekster, mens grupper som er migrert til
        stabile i18n-nøkler oversettes her i klienten. */}
    <CardHead icon="bell" title={t('home.followup.title')}  help={t('helpTopics.followup.body')} helpTitle={t('helpTopics.followup.title')}/>
    {visibleGroups.length === 0 && <div className="col center" style={{ textAlign: 'center', padding: '14px 6px 6px', gap: 6 }}>
      <img src={(window.__resources && window.__resources.plummeZen) || 'src/img/PlummeZen-450.webp'} alt="" style={{ width: 150, height: 'auto', display: 'block' }} />
      <div className="t-h3" style={{ fontSize: 16 }}>{t('home.followup.allClear')}</div>
      <div className="t-sm muted" style={{ fontWeight: 600, maxWidth: 280 }}>{t('home.followup.allClearSub')}</div>
    </div>}
    {visibleGroups.map((group, index) => <React.Fragment key={group.id || group.title}>
      {index > 0 && <hr className="hairline" style={{ margin: '18px 0' }} />}
      <div className="row between" style={{ marginBottom: 8 }}><span className="t-h3">{group.title}</span><span className="badge badge-neutral">{group.items.length}</span></div>
      <div className="col gap-8">
        {group.visibleItems.map((item, itemIndex) => <FollowupRow key={`${group.id || group.title}-${itemIndex}`} item={item} onOpen={openRoute} />)}
      </div>
      {group.route && <Button variant="primary" className="btn-block mt-12" onClick={() => openRoute(group)}>{group.actionLabel || t('home.followup.next')}</Button>}
    </React.Fragment>)}
    {totalItems > MAX_VISIBLE_FOLLOW_UP_ITEMS && <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
      <Button variant="ghost" size="sm" iconRight="arrowRight" onClick={() => setModalOpen(true)}>{t('home.followup.showMore')}</Button>
    </div>}
    <Modal open={modalOpen} onClose={() => setModalOpen(false)} max={620} title={t('home.followup.modalTitle')} titleRight={<span className="badge badge-neutral">{totalItems}</span>}>
      <div className="col gap-22" style={{ paddingBottom: 18 }}>
        {groups.map((group) => <div key={group.id || group.title}>
          <div className="row between" style={{ marginBottom: 10, alignItems: 'center', gap: 12 }}>
            <div className="row" style={{ alignItems: 'center', gap: 10 }}>
              <span className="t-h3">{group.title}</span>
              <span className="badge badge-neutral">{group.items.length}</span>
            </div>
            {group.route && <Button variant="secondary" size="sm" iconRight="arrowRight" onClick={() => openRoute(group)}>{group.actionLabel || t('home.followup.next')}</Button>}
          </div>
          <div className="col gap-8">{group.items.map((item, itemIndex) => <FollowupRow key={`${group.id || group.title}-modal-${itemIndex}`} item={item} onOpen={openRoute} />)}</div>
        </div>)}
      </div>
    </Modal>
  </Card>;
}

function EqTile({ label, amount, tone, op, pct }) {
  return <div className="eq-tile" style={{ background: tone.bg, borderColor: tone.bd }}>
    <div className="t-xs row" style={{ fontWeight: 800, color: tone.fg, gap: 6, alignItems: 'center', justifyContent: 'space-between' }}>
      <span className="row" style={{ gap: 6, alignItems: 'center' }}>{op && <span className="eq-op-inline" style={{ color: tone.fg }}>{op}</span>}{label}</span>
      {pct != null && <span style={{ fontWeight: 900, fontSize: 12, color: tone.fg }}>{pct} %</span>}
    </div>
    <div className="t-num" style={{ fontSize: 17, fontWeight: 900, marginTop: 6, color: 'var(--ink)', textAlign: 'right' }}>{formatHomeCurrency(amount)}</div>
  </div>;
}
// `kind` er 'sales' | 'purchases' — stabil id, ikke den oversatte tittelen.
function OverviewSide({ icon, kind, innenFrist, forfalt, importedWithin = 0, importedOverdue = 0 }) {
  const total = innenFrist + forfalt;
  const importedTotal = importedWithin + importedOverdue;
  const pctFrist = total > 0 ? Math.round(innenFrist / total * 100) : 100;
  const pctForfalt = total > 0 ? 100 - pctFrist : 0;
  const title = t('home.overview.' + kind);
  const label = t(kind === 'sales' ? 'home.overview.outstandingSales' : 'home.overview.outstandingPurchases');
  const help = t(kind === 'sales' ? 'home.overview.salesHelp' : 'home.overview.purchasesHelp');
  return <div className="grow" style={{ minWidth: 0 }}>
    <div className="row gap-10" style={{ marginBottom: 14 }}>
      <span className="icon-chip"><Icon name={icon} size={18} /></span>
      <span className="t-h3" style={{ fontSize: 16 }} data-help={help} data-help-title={t('home.overview.helpTitle', { what: title.toLowerCase() })}>{title}</span>
    </div>
    <div className="t-sm muted" style={{ fontWeight: 700, marginBottom: 8 }}>{label}</div>
    <div className="grow ov-statusbar" title={t('home.overview.statusTitle', { within: pctFrist, over: pctForfalt })}>
      {total === 0 ? <span style={{ width: '100%', background: 'var(--success)' }} /> : <>
        {pctFrist > 0 && <span style={{ width: pctFrist + '%', background: 'var(--success)' }} />}
        {pctForfalt > 0 && <span style={{ width: pctForfalt + '%', background: 'var(--warning)' }} />}
      </>}
    </div>
    <div className="eq-stack mt-16">
      <div className="eq-row">
        <EqTile label={t('home.overview.withinDue')} amount={innenFrist} pct={pctFrist} tone={{ bg: 'var(--success-bg)', bd: 'var(--success-bd)', fg: 'var(--success)' }} />
        <span className="eq-op">+</span>
        <EqTile label={t('home.overview.overdue')} amount={forfalt} pct={pctForfalt} tone={{ bg: 'var(--warning-bg)', bd: 'var(--warning-bd)', fg: 'var(--warning)' }} />
      </div>
      <div className="eq-row eq-row-total">
        <EqTile op="=" label={label} amount={total} tone={{ bg: 'var(--plum-100)', bd: 'var(--plum-200)', fg: 'var(--plum-700)' }} />
      </div>
      <div className="row gap-10" style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <span className="t-xs muted" style={{ fontWeight: 700 }}>{t('home.overview.inclVat')}</span>
        {importedTotal > 0 && <span className="t-xs muted" style={{ textAlign: 'right', fontWeight: 700 }}>{t('home.overview.importedSubledger', { amount: formatHomeCurrency(importedTotal) })}</span>}
      </div>
    </div>
  </div>;
}
function OverviewCard({ overview }) {
  return <Card className="ov-card" style={{ position: 'relative' }}>
    <img src={(window.__resources && window.__resources.plommePie) || "src/img/plommePie-264.webp"} alt="" className="ov-mascot" />
    <div className="ov-split">
      <OverviewSide icon="fileText" kind="sales" innenFrist={overview.salesCurrent} forfalt={overview.salesOverdue} importedWithin={overview.importedSalesCurrent} importedOverdue={overview.importedSalesOverdue} />
      <div className="ov-divider" />
      <OverviewSide icon="bag" kind="purchases" innenFrist={overview.purchasesCurrent} forfalt={overview.purchasesOverdue} importedWithin={overview.importedPurchasesCurrent} importedOverdue={overview.importedPurchasesOverdue} />
    </div>
  </Card>;
}
const viewLabel = (view) => t('home.viewLabel.' + view);

// Saldo-kortet. Kontoene og saldoene kommer fra backend-modellen (saldobalansen), og
// utvalget lagres pa brukerprofilen — ikke i localStorage — sa det folger brukeren
// mellom enheter. Utvalget holdes ogsa lokalt mens lagringen gar, slik at avkryssingen
// ikke hopper tilbake mens vi venter pa Firestore.
// Raden er en hovedbokskonto, ikke en betalingsmate, sa kontokoden staar alltid med:
// den er det som skiller to rader med samme navn fra hverandre.
const saldoAccountLabel = (account) =>
  [account.name || t('home.balances.accountFallback', { code: account.code }), account.code].join(' · ');
function SaldoCard() {
  const model = window.PlummeBackend?.getHomeBalancesModel?.() || null;
  const accounts = model?.accounts || [];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(null);
  const latest = React.useRef(null);
  const selected = pending || model?.selected || [];
  // Bare siste klikk far slippe det lokale utvalget tilbake til modellen. Ellers ville
  // et tidlig svar vist forrige avkryssing igjen mens neste lagring fortsatt gikk.
  const save = (next, call) => {
    latest.current = next;
    setPending(next);
    const done = (failed) => { if (latest.current === next) setPending(null); if (failed) toast(t('home.balances.saveFailed'), 'error'); };
    Promise.resolve(call()).then(() => done(false), () => done(true));
  };
  const toggle = (code) => {
    const next = selected.includes(code) ? selected.filter((entry) => entry !== code) : [...selected, code];
    save(next, () => window.PlummeBackend?.setHomeBalanceAccounts?.(next));
  };
  const addAccount = (code) => {
    setQuery('');
    save([...selected, code], () => window.PlummeBackend?.addHomeBalanceAccount?.(code));
  };
  const removeAccount = (code) => {
    save(selected.filter((entry) => entry !== code), () => window.PlummeBackend?.removeHomeBalanceAccount?.(code));
  };
  const hits = query.trim() ? (window.PlummeBackend?.getHomeBalanceAccountOptions?.(query) || []) : [];
  const shown = accounts.filter((account) => selected.includes(account.code));
  const total = shown.reduce((sum, account) => sum + account.balance, 0);
  return model?.loading ? <Card><window.BrandLoading text={t('home.journalLoading')} compact /></Card> : model?.blocked ? <Card><EmptyState acc="none" mood="concerned" title={t('home.incompleteBasisTitle')} text={t('home.incompleteBasisText')} compact /></Card> : <Card>
    <CardHead icon="wallet" title={t('home.balances.title')} help={t('home.balances.help')} right={<div className="popwrap">
      <IconButton name="settings" size="sm" variant="ghost" onClick={() => setOpen((value) => !value)} title={t('home.balances.choose')} />
      {open && <React.Fragment>
        <div className="pop-scrim" onClick={() => setOpen(false)} />
        <div className="pop pop-menu" style={{ right: 0, minWidth: 260, maxWidth: 'calc(100vw - 32px)' }}>
          <div style={{ padding: '6px 10px' }}>
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('home.balances.searchPlaceholder')} style={{ fontSize: 13 }} />
          </div>
          {query.trim() && <div className="col" style={{ maxHeight: 160, overflowY: 'auto' }}>
            {hits.length === 0 && <div className="t-xs muted" style={{ padding: '6px 10px' }}>{t('home.balances.noHits')}</div>}
            {hits.map((hit) => <button key={hit.code} type="button" className="row gap-8" style={{ padding: '7px 10px', width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', fontSize: 13.5 }} onClick={() => addAccount(hit.code)}>
              <Icon name="plus" size={14} /><b>{hit.code}</b><span className="muted" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hit.name}</span>
            </button>)}
          </div>}
          <div className="t-xs muted" style={{ fontWeight: 800, padding: '6px 10px' }}>{t('home.balances.choose')}</div>
          {accounts.map((account) => <div key={account.code} className="row gap-8" style={{ padding: '6px 10px', alignItems: 'center' }}>
            <span className="grow" style={{ minWidth: 0 }}><Checkbox checked={selected.includes(account.code)} onChange={() => toggle(account.code)}>{saldoAccountLabel(account)}</Checkbox></span>
            {/* Driftskonto og kontantkasse kan skjules, men ikke fjernes — se backend. */}
            {!account.fixed && <IconButton name="trash" size="sm" variant="ghost" title={t('home.balances.remove')} onClick={() => removeAccount(account.code)} />}
          </div>)}
        </div>
      </React.Fragment>}
    </div>} />
    {shown.length === 0
      ? <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('home.balances.noneSelected')}</div>
      : <div className="col">
        {shown.map((account, index) => <React.Fragment key={account.code}>
          {index > 0 && <hr className="hairline" />}
          <div className="row between gap-12" style={{ padding: '10px 2px' }}>
            <span className="t-sm muted" style={{ fontWeight: 700, minWidth: 0 }}>{saldoAccountLabel(account)}</span>
            <b className="t-h3 t-num" style={{ fontSize: 16 }}>{formatHomeCurrency(account.balance)}</b>
          </div>
        </React.Fragment>)}
        <hr className="hairline" />
        <div className="row between gap-12" style={{ padding: '10px 2px 2px' }}>
          <span className="t-sm" style={{ fontWeight: 800 }}>{t('home.balances.total')}</span>
          <b className="t-h3 t-num" style={{ fontSize: 17 }}>{formatHomeCurrency(total)}</b>
        </div>
      </div>}
  </Card>;
}

function DashboardContent({ setRoute, model }) {
  const chart = Array.isArray(model?.chart) && model.chart.length ? model.chart : EMPTY_HOME_MODEL.chart;
  const lastIdx = (() => { for (let i = chart.length - 1; i >= 0; i--) { if (chart[i].inc || chart[i].cost) return i; } return chart.length - 1; })();
  const lastM = chart[lastIdx] || { inc: 0, cost: 0 };
  const prevM = chart[lastIdx - 1] || { inc: 0, cost: 0 };
  const [view, setView] = useState('period');
  const [anaMode, setAnaMode] = useState('ring');
  const [anaOpen, setAnaOpen] = useState(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drill, setDrill] = useState(null);
  const togM = (i) => setSelMonths((s) => s.includes(i) ? s.filter((x) => x !== i) : [...s, i].sort((a, b) => a - b));
  const selSum = selMonths.length ? selMonths.reduce((a, i) => ({ inc: a.inc + chart[i].inc, cost: a.cost + chart[i].cost }), { inc: 0, cost: 0 }) : null;
  const ANA = { ring: t('home.ana.ring'), gauge1: t('home.ana.gauge1'), gauge2: t('home.ana.gauge2') };
  return <div>
    {/* greeting + quick actions */}
    <div className="row wrap gap-14" style={{ margin: '2px 2px 22px', alignItems: 'center' }}>
      <div><div className="t-h1">{t('home.greeting')}</div></div>
      <div className="row wrap gap-10 home-quick-actions">
        <Button variant="secondary" icon="fileText" onClick={() => setRoute('faktura')}
        data-help-title={t('home.registerSale')} data-help={t('home.registerSaleHelp')}>{t('home.registerSale')}</Button>
        <Button variant="secondary" icon="bag" onClick={() => setRoute('nyttkjop')}
        data-help-title={t('home.registerPurchase')} data-help={t('home.registerPurchaseHelp')}>{t('home.registerPurchase')}</Button>
        <Button icon="checkCircle" onClick={() => setRoute('aiautopilot')} className="ap-status-btn"
        data-help-title={t('nav.aiBookkeeper')} data-help={t('home.aiHelp')}>{t('nav.aiBookkeeper')}<AutopilotStatus followup={model.followup} /></Button>
      </div>
    </div>

    {/* Dialogen med byraaet. Kortet rendrer seg selv bort naar foretaket ikke
        har et aktivt oppdrag, saa Hjem staar uendret for alle andre. */}
    {window.FirmChatHomeCard ? <window.FirmChatHomeCard /> : null}
    {window.FirmChatLoginModal ? <window.FirmChatLoginModal /> : null}
    {window.DagsoppgjorHomeCard && <window.DagsoppgjorHomeCard setRoute={setRoute} />}
    <OnboardingFollowupCard setRoute={setRoute} />
    <div className="dash-grid">
      <div className="col gap-22">
        {/* chart card — control integrated into header */}
        <Card>
          <div className="row between wrap gap-12" style={{ marginBottom: 16 }}>
            <div className="row gap-7">
              <button className={'seg-btn' + (view === 'period' ? ' on' : '')} onClick={() => setView('period')}>{t('home.view.period')}</button>
              <div className="popwrap">
                <button className={'seg-btn' + (view === 'analysis' ? ' on' : '')} style={{ whiteSpace: 'nowrap' }} onClick={() => { if (view !== 'analysis') { setView('analysis'); setAnaOpen(true); } else setAnaOpen((o) => !o); }}>{t('home.view.analysis')}<Icon name="chevDown" size={15} /></button>
                {anaOpen && <React.Fragment><div className="pop-scrim" onClick={() => setAnaOpen(false)} />
                  <div className="pop pop-menu" style={{ minWidth: 220 }}>
                    {Object.entries(ANA).map(([k, lbl]) => <button key={k} onClick={() => { setView('analysis'); setAnaMode(k); setAnaOpen(false); }}>{lbl}{anaMode === k && view === 'analysis' && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>)}
                  </div></React.Fragment>}
              </div>
              <button className={'seg-btn' + (view === 'cashflow' ? ' on' : '')} onClick={() => setView('cashflow')}>{t('home.view.cashflow')}</button>
            </div>
            <div className="row gap-16">
              <span className="row gap-6 t-sm" style={{ fontWeight: 700 }}><span style={{ width: 11, height: 11, borderRadius: 'var(--r-3xs)', background: 'var(--success)' }} />{t('home.legend.income')}</span>
              <span className="row gap-6 t-sm" style={{ fontWeight: 700 }}><span style={{ width: 11, height: 11, borderRadius: 'var(--r-3xs)', background: 'var(--warning)' }} />{t('home.legend.cost')}</span>
              {view === 'analysis' && anaMode === 'ring' && <span className="row gap-6 t-sm" style={{ fontWeight: 700 }}><span style={{ width: 11, height: 11, borderRadius: 'var(--r-3xs)', background: 'var(--plum-600)' }} />{t('home.legend.result')}</span>}
            </div>
          </div>
          <h3 className="t-h3" style={{ marginBottom: 2 }}>{view === 'analysis' ? (anaMode === 'gauge2' ? ANA.gauge2 : anaMode === 'gauge1' ? ANA.gauge1 : viewLabel('analysis')) : viewLabel(view)}</h3>
          <div className="t-sm muted" style={{ marginBottom: 16 }}>{view === 'analysis' && anaMode !== 'ring' ? t('home.sub.yoyArrow') : t('home.sub.exVat')}</div>
          {view === 'analysis'
            ? (anaMode === 'gauge1' ? <GaugeRow data={buildDistributionGauges(model)} /> : anaMode === 'gauge2' ? <GaugeRow data={buildFinancialGauges(model)} /> : <Donut drill={drill} setDrill={setDrill} income={model.summary.income} costs={model.summary.costs} result={model.summary.result} />)
            : <div className="home-chart-box" style={{ aspectRatio: '2 / 1' }}>{view === 'period' ? <PeriodChart chart={chart} sel={selMonths} onToggle={togM} /> : <CashflowChart chart={chart} sel={selMonths} onToggle={togM} />}</div>}
          {!(view === 'analysis' && anaMode !== 'ring') && <React.Fragment>
          {view !== 'analysis' && selMonths.length > 0 && <div className="row gap-8 wrap" style={{ marginTop: 14, alignItems: 'center' }}>
            <span className="t-sm" style={{ fontWeight: 800 }}>{t('home.selectedMonths')}</span>
            {selMonths.map((i) => <button key={i} className="donut-leg" onClick={() => togM(i)}>{homeMonthLabel(i)} ✕</button>)}
            <button className="link-reset" onClick={() => setSelMonths([])}>{t('home.reset')}</button>
          </div>}
          <div className="grid-3" style={{ gap: 12, marginTop: 18 }}>
            {(() => {
              const useSel = view !== 'analysis' && selSum;
              const base = useSel ? selSum : { inc: model.summary.income, cost: model.summary.costs };
              const defs = [[t('home.legend.income'), 'green', 'inc'], [t('home.legend.cost'), 'peach', 'cost'], [t('home.legend.result'), 'plum', 'net']];
              const pick = (o, k) => k === 'inc' ? o.inc : k === 'cost' ? o.cost : o.inc - o.cost;
              const drillOf = { inc: 'inc', cost: 'cost', net: null };
              return defs.map(([label, tint, key]) => {
                const tr = useSel ? null : pctChg(pick(lastM, key), pick(prevM, key));
                const sub = useSel
                  ? <span className="muted" style={{ fontWeight: 700 }}>{t(selMonths.length === 1 ? 'home.sumOfMonth' : 'home.sumOfMonths', { n: selMonths.length })}</span>
                  : tr == null
                    ? <span className="muted" style={{ fontWeight: 700 }}>{t('home.noPrevPeriod')}</span>
                    : <React.Fragment>{pctStr(tr)} %<span className="muted" style={{ marginLeft: 5, fontWeight: 700 }}>{t('home.fromPrevMonth')}</span></React.Fragment>;
                const card = <KPI tint={tint} label={label} value={formatHomeCurrency(pick(base, key))} trend={useSel ? undefined : tr} sub={sub} />;
                return view === 'analysis'
                  ? <div data-app-control="true" key={label} style={{ cursor: 'pointer' }} onClick={() => setDrill(drillOf[key])} title={t('home.showDistribution')}>{card}</div>
                  : <React.Fragment key={label}>{card}</React.Fragment>;
              });
            })()}
          </div>
          </React.Fragment>}
        </Card>

        {/* combined sales + purchase overview */}
        <OverviewCard overview={model.overview} />

        {/* deadlines */}
        <Card style={{ position: 'relative', overflow: 'visible' }}>
          <img src={(window.__resources && window.__resources.plommeFrist) || 'src/img/plommeFrist-390.webp'} alt="" className="home-deadlines-mascot" style={{ position: 'absolute', top: -76, left: '50%', transform: 'translateX(-50%)', width: 130, height: 'auto', pointerEvents: 'none', zIndex: 3, filter: 'drop-shadow(0 4px 10px color-mix(in srgb, var(--shadow-tint) 18%, transparent))' }} />
          <CardHead icon="calendar" title={t('home.deadlines.title')} help={t('home.deadlines.help')} />
          <div className="col gap-10">
            {model.deadlines.length === 0 && <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('home.deadlines.none')}</div>}
            {model.deadlines.map((d) => <div key={d.id} className="row between gap-14 wrap" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
              <div className="row gap-12" style={{ minWidth: 0 }}>
                <span className="icon-chip" style={{ background: 'var(--panel)' }}><Icon name="calendar" size={17} /></span>
                <div style={{ minWidth: 0 }}><div className="t-h3" style={{ fontSize: 14.5 }} data-help={deadlineHelpText(d)} data-help-title={deadlineDisplayName(d)}>{deadlineDisplayName(d)}</div><div className="t-sm muted">{deadlineDisplayDate(d)}</div></div>
              </div>
              <div className="row gap-10"><StatusBadge status={d.status} /><Button variant="secondary" size="sm" onClick={() => setRoute(routeForDeadline(d))} data-help={t('helpTopics.followup.body')} data-help-title={t('helpTopics.followup.title')}>{t('home.deadlines.seeStatus')}</Button></div>
            </div>)}
          </div>
        </Card>
      </div>

      {/* right rail */}
      <div className="col gap-22">
        <Assistant setRoute={setRoute} />
        <FollowupCard followup={model.followup} setRoute={setRoute} />
        <Card>
          <CardHead icon="percent" title={t('home.fees.title')} help={t('home.fees.help')} />
          <div>
            {(model.publicFees.rows || []).length === 0 && <div className="t-sm muted" style={{ fontWeight: 700 }}>{t('home.fees.none')}</div>}
            {(model.publicFees.rows || []).map((row, index) => <React.Fragment key={row.id || row.label}>
              {index > 0 && <hr className="hairline" />}
              <div className="row between" style={{ padding: index === 0 ? '6px 2px 14px' : '14px 2px 6px' }}>
                <span className="t-sm muted" style={{ fontWeight: 700 }}>{row.label}</span>
                <b className="t-h3 t-num" style={{ fontSize: 16 }}>{formatHomeCurrency(row.amount)}</b>
              </div>
            </React.Fragment>)}
          </div>
        </Card>
        <SaldoCard />
      </div>
    </div>
  </div>;
}

function Dashboard({ setRoute }) { const model = getHomeModel(); if (model?.loading) return <Card><window.BrandLoading text={t('home.journalLoading')} compact /></Card>; return model?.blocked ? <Card><EmptyState acc="none" mood="concerned" title={t('home.incompleteBasisTitle')} text={t('home.incompleteBasisText')} compact /></Card> : <DashboardContent setRoute={setRoute} model={model} />; }
Object.assign(window, { Dashboard, SaldoCard });
